import { useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  RecommendedGroupClassModalAnalyticsEvent,
  RecommendedGroupClassModalAnalyticsSource,
} from "@/constants/analytics/Analytics";
import { GroupClassRegistrationModalTestID } from "@/constants/TestID";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import { Button, FAIconName, Text } from "@/ui/core";
import SelectableCard from "@/ui/core/SelectableCard";
import { Box, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

enum DeclineReason {
  ConflictWithSchedule = "Conflict with my schedule",
  DontSeeValue = "I don't see the value",
  FeelingOverwhelmed = "I'm overwhelmed right now",
}

interface GroupClassDeclineReasonFormProps extends ViewWithTestID {
  onBack: () => void;
  onSubmit: () => void;
}

const GroupClassDeclineReasonForm = ({
  onBack,
  onSubmit,
  testID,
}: GroupClassDeclineReasonFormProps) => {
  const [selectedReason, setSelectedReason] = useState<
    DeclineReason | undefined
  >(undefined);
  const { showToast } = useToast();
  const { track } = useAnalytics();

  const handleSubmit = () => {
    if (isNil(selectedReason)) return;

    track(RecommendedGroupClassModalAnalyticsEvent.DeclineReasonSubmitted, {
      [AnalyticsFieldName.Reason]: selectedReason,
      [AnalyticsFieldName.Source]: RecommendedGroupClassModalAnalyticsSource,
    });

    showToast({
      text: "Your feedback was submitted",
      variant: "success",
    });

    onSubmit();
  };

  const handleBackPress = () => {
    track(RecommendedGroupClassModalAnalyticsEvent.PickATimePressed, {
      [AnalyticsFieldName.Source]: RecommendedGroupClassModalAnalyticsSource,
    });
    onBack();
  };

  const baseTestID = joinTestIDs(
    testID,
    GroupClassRegistrationModalTestID.declineReasonForm,
  );

  return (
    <VStack alignItems="center" gap={24} testID={baseTestID} width="100%">
      <VStack gap={12}>
        <Text testID={joinTestIDs(baseTestID, "header")} variant="h5">
          Thanks for letting us know!
        </Text>
        <Text
          color={ColorToken.NeutralPrimary}
          testID={joinTestIDs(baseTestID, "subtext")}
          variant="body"
        >
          Tell us a bit more so we can better cater group treatment to your
          needs.
        </Text>
      </VStack>

      <Box
        backgroundColor={ColorToken.TertiaryMarigoldBackground}
        borderRadius={12}
        paddingHorizontal={18}
        paddingVertical={18}
        testID={joinTestIDs(baseTestID, "why-groups-section")}
      >
        <VStack gap={12}>
          <Text
            testID={joinTestIDs(baseTestID, "why-groups-title")}
            variant="captionMedium"
          >
            Why groups matter
          </Text>
          <Text
            testID={joinTestIDs(baseTestID, "why-groups-text")}
            variant="caption"
          >
            Groups are a core component of treatment where you&apos;ll get most
            of the education and skills for your recovery toolkit. Your
            individual sessions will then help you apply what you&apos;ve
            learned.
          </Text>
        </VStack>
      </Box>

      <VStack
        gap={18}
        testID={joinTestIDs(baseTestID, "reasons-section")}
        width="100%"
      >
        <Text testID={joinTestIDs(baseTestID, "question")} variant="bodyMedium">
          Why didn&apos;t those times work for you?
        </Text>
        <VStack gap={12}>
          {Object.values(DeclineReason).map((reason) => (
            <SelectableCard
              accessibilityLabel={reason}
              isSelected={selectedReason === reason}
              key={reason}
              label={reason}
              onSelect={() => setSelectedReason(reason)}
              testID={joinTestIDs(baseTestID, "reason", reason)}
            />
          ))}
        </VStack>
      </VStack>

      <VStack alignItems="center" gap={18}>
        <Button
          isDisabled={isNil(selectedReason)}
          isFullWidth
          leftIcon={FAIconName.Check}
          onPress={handleSubmit}
          testID={joinTestIDs(baseTestID, "submit")}
          title="Submit"
          variant="primary"
        />
        <Button
          leftIcon={FAIconName.ChevronLeft}
          onPress={handleBackPress}
          testID={joinTestIDs(baseTestID, "back")}
          title="Pick a time"
          variant="tertiary"
        />
      </VStack>
    </VStack>
  );
};

export type { GroupClassDeclineReasonFormProps };
export { DeclineReason };
export default GroupClassDeclineReasonForm;
